import '../searchinput/ui-searchinput.js';
import '../modal/ui-modal-controller.js';
import '../option/ui-option-basic.js';
import '../option/ui-option-account.js';
import '../option/ui-option-multipayment.js';
import '../option/ui-option-icon.js';
import { UIElement } from '../ui-element.js';
import {
    debounce,
    appendModalControllerIfRequired,
    makePopoverHandler,
} from '../../global/helpers.js';
import { isKeyPressed, keyCodes, TabIndex } from '../../global/keyboard.js';
import {
    addOutsideClickHandler,
    closestByCond,
    dispatchNativeEvent,
    isParentOf,
    getBaseGridPoint,
} from '../../global/ui-helpers.js';
import { Digest } from '../../global/digest.js';
import { Labels } from '../../global/labels.js';
import styles from './ui-autocomplete.css';
import { isAccountTypeLayout } from './ui-autocomplete.helpers.js';

/**
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIAutocomplete
 * @element ui-autocomplete
 * @classdesc Represents a class for <code>ui-autocomplete</code> element.
 * After typing, it suggests an option to choose for auto-completion.
 * @fires event:search
 * @fires event:pick
 * @listens onkeydown
 * @listens event:clear
 * @property {"account" | "basic" | "multipayment" | "icon" | "search"} [layout="basic"]
 *  {@attr layout}
 * Layout for options inside
 *  {@desc account}
 *  {@desc basic}
 *  {@desc multipayment}
 *  {@desc icon}
 *  {@desc search}
 * @property {number} [maxlength] {@attr maxlength} Sets how many options should be shown in
 * popover, the rest will be scrolled.
 * @property {number} [minmatch=-1] {@attr minmatch} This determines minimum number
 * of matching characters
 * @property {number} [debounce=0] {@attr debounce} Debounce time to requests.
 * @property {number} [external] {@attr external} Make the component controllable by external data
 * provider.
 * @property {("basic")} [type] {@attr type} Type of the autocomplete popover.
 * @property {string} value Value of the field
 * @property {string} option Tag name of the option
 * @property {boolean} touched True if user touched the autocomplete.
 * @property {string} uuid {@readonly} Unique identifier of the autocomplete input.
 * @property {HTMLInputElement} control {@readonly}
 * @property {UISearchInput} search {@readonly} Shortcut to UISearchInput child element.
 * @property {Function} comparator Comparator function to find matches.
 * @property {UIDatalist} datalist {@readonly} Shortcut to UIDatalist child element.
 * @property {HTMLUListElement} options {@readonly} Shortcut to list of entries.
 * @property {HTMLDivElement} popoverElement {@readonly} Shortcut to a popover element.
 * @property {HTMLDivElement} assistant {@readonly} Shortcut to an assistant element.
 * @property {HTMLDivElement} hint {@readonly} Shortcut to hint element (removed after input).
 * @slot
 * @example
 * <ui-field>
 *   <ui-autocomplete>
 *     <input type="search">
 *     <ui-datalist>
 *       <option value="value" data-whatever="whatever">
 *       <option value="somevalue" data-whatever="somedata">
 *     </ui-datalist>
 *   </ui-autocomplete>
 * </ui-field>
 */
class UIAutocomplete extends UIElement {
    /**
     * @type {Props}
     * @readonly
     */
    static get props() {
        return {
            attributes: {
                layout: { type: String, default: UIAutocomplete.Layout.Basic },
                minmatch: { type: Number, default: -1 },
                maxlength: Number,
                external: Boolean,
                debounce: { type: Number, default: 0 },
            },
            children: {
                search: 'ui-searchinput',
                control: '.ui-autocomplete__control',
                datalist: 'ui-datalist',
                assistant: '.ui-autocomplete__assistant',
                hint: '.ui-autocomplete__hint',
            },
        };
    }

    /**
     * @type {Record<string, string>}
     */
    static get Layout() {
        return {
            Account: 'account',
            Basic: 'basic',
            Multipayment: 'multipayment',
            Search: 'search',
        };
    }

    /**
     * @type {Record<string, string>}
     */
    static get Type() {
        return {
            Basic: 'basic',
        };
    }

    /**
     * @type {MutationObserverInit}
     * @private
     */
    static get ObserveOptions() {
        return {
            childList: true,
            subtree: true,
            attributes: true,
            characterData: true,
        };
    }

    /**
     * Provides getter and setter for "value" property
     * and link it to the "value" attribute
     * syncs with inner input
     */
    get value() {
        return this.control.value;
    }
    set value(value) {
        const input = this.control;
        if (input.value === value) {
            return;
        }
        input.value = value;
    }

    get option() {
        return `ui-option-${this.layout}`;
    }
    set option(value) {
        if (!value || value.indexOf('ui-option-') !== 0) {
            this.layout = null;
        }
        this.layout = value.replace('ui-option-', '');
    }

    get options() {
        return this.popoverElement?.querySelector('.ui-autocomplete__options');
    }

    get popoverElement() {
        if (this._detachedPopover) {
            return this._detachedPopover;
        }
        return this.querySelector('.ui-autocomplete__popover');
    }

    get type() {
        if (this.layout === UIAutocomplete.Layout.Search) {
            return this.layout;
        }
        const type = this.getAttribute('type');
        if (
            [UIAutocomplete.Type.Basic, UIAutocomplete.Type.Plain].includes(
                type
            )
        ) {
            return type;
        }
        this.removeAttribute('type');
        const elementFactory = customElements.get(this.option);
        if (!elementFactory) {
            return null;
        }
        return elementFactory.POPOVER_TYPE;
    }
    set type(value) {
        // do nothing
    }

    get comparator() {
        const el = customElements.get(this.option);
        return el && typeof el.prototype.findMatch === 'function'
            ? el.prototype.findMatch
            : () => true;
    }
    set comparator(value) {
        // do nothing
    }

    /**
     * @type {string}
     */
    get uuid() {
        this._uuid ??= Digest.randomId();
        return this._uuid;
    }

    /**
     * @type {Labels}
     * @readonly
     */
    static get labels() {
        return Labels.attach('ui-autocomplete', {
            hint: 'Use up and down arrows to review and enter to select.',
            suggestions: 'Suggestions',
            suggestionsFound: 'Suggestions displayed: ',
            noSuggestionsFound: 'No suggestions displayed',
        });
    }

    /**
     *  Returns the list of options.
     *  @param {number} [index] If index is set return options with index.
     *  @returns {UIElement}
     */
    getOptions(index) {
        const options =
            this.popoverElement?.querySelectorAll('.ui-autocomplete__option') ||
            [];
        return index > -1 ? options[index] : options;
    }

    /**
     * Get popover height in pixels.
     * @returns {boolean}
     */
    isValueValid() {
        const value = this.control.value;
        if (!(this.minmatch > -1)) {
            return true;
        }
        return value && value.length >= this.minmatch;
    }

    /**
     * Get popover height in pixels.
     * @returns {number}
     */
    getPopoverHeight() {
        const options = this.popoverElement?.querySelectorAll(
            '.ui-autocomplete__option, .ui-autocomplete__group > [optgroup]'
        );
        if (!options?.length) {
            this.closePopover();
            return 0;
        }

        const limit = this.maxlength
            ? Math.min(this.maxlength, options.length)
            : options.length;

        const lastVisibleItem = options[limit - 1];
        return lastVisibleItem.offsetTop + lastVisibleItem.clientHeight;
    }

    /**
     * Sets popover height in pixels.
     * @param {number} value
     */
    setPopoverHeight(value) {
        /** @type {HTMLElement} */
        const options = this.popoverElement?.querySelector(
            '.ui-autocomplete__options'
        );
        if (!options) {
            return;
        }
        const newHeight = value > 0 ? `${value}px` : null;
        const oldHeight = options.style.maxHeight;

        if (oldHeight === newHeight) {
            return;
        }

        options.style.maxHeight = newHeight;
    }

    /**
     * Sets popover width in pixels.
     * @param {number} value
     */
    setPopoverWidth(value) {
        if (!this.popoverElement) {
            return;
        }
        this.popoverElement.style.width = value > 0 ? `${value}px` : null;
    }

    /**
     * Checks if autocomplete has options.
     * @returns {boolean}
     */
    hasOptions() {
        return !!this.popoverElement?.querySelector('.ui-autocomplete__option');
    }

    /**
     * Checks if the popover is visible.
     * @returns {boolean}
     */
    isPopoverVisible() {
        return !!this._detachedPopover;
    }

    /**
     * Opens detached popover.
     * @fires event:modal-open
     */
    openDetachedPopover() {
        if (this._detachedPopover) {
            return;
        }
        this._detachedPopover = this.popoverElement;
        this.dispatchCustomEvent('modal-open', {
            type: 'popover',
            content: this.popoverElement,
            params: {
                disableKeyboardTrapping: true,
                classList: {
                    '-autocomplete': true,
                    '-basic': this.type === UIAutocomplete.Type.Basic,
                    '-search': this.layout === UIAutocomplete.Layout.Search,
                },
                position: makePopoverHandler(this, {
                    supportMiddleAlignment: false,
                    supportTopAlignment:
                        this.layout !== UIAutocomplete.Layout.Search,
                    topOffset: () => {
                        const hasScrollbar =
                            this.options &&
                            this.options.scrollHeight >
                                this.options.clientHeight;
                        return hasScrollbar ? getBaseGridPoint() : 0;
                    },
                    popoverHeight: () => {
                        const h = this.resolvePopoverHeight();
                        this.setPopoverHeight(h);
                        this.setPopoverWidth(this.offsetWidth);
                        return h;
                    },
                }),
                target: this,
                onClose: () => this.closePopover(),
                onOpen: () => this.control.focus(),
            },
        });
        this.classList.add('-active');
    }

    /**
     * @private
     * @returns {number}
     */
    resolvePopoverHeight() {
        switch (this.layout) {
            case UIAutocomplete.Layout.Search:
                return this.getPopoverHeight();
            default:
                const popoverHeight = this.getPopoverHeight();

                if (this.maxlength) {
                    return popoverHeight;
                }

                const controlRect = this.control.getBoundingClientRect();
                const yMargin = getBaseGridPoint() * 3;

                const spaceAvailableAbove = Math.max(
                    0,
                    controlRect.top - yMargin
                );
                const spaceAvailableBelow = Math.max(
                    0,
                    window.innerHeight - controlRect.bottom - yMargin
                );

                if (
                    popoverHeight <= spaceAvailableBelow ||
                    popoverHeight <= spaceAvailableAbove
                ) {
                    return popoverHeight;
                }

                return Math.max(spaceAvailableAbove, spaceAvailableBelow);
        }
    }

    /**
     * Closes detached popover.
     */
    closeDetachedPopover() {
        if (!this._detachedPopover) {
            return;
        }
        const modal = this._detachedPopover.closest('ui-modal');
        if (modal) {
            this._detachedPopover = null;
            modal.close();
        }
        this.classList.remove('-active');
    }

    /**
     * Opens dropdown popup.
     */
    openPopover() {
        if (this.isPopoverVisible() || this.control?.hasAttribute('readonly')) {
            return;
        }
        this.popoverElement['offsetWidth'].toString();
        this.options.addEventListener('click', this.handleClickOnOption);
        this.setPopoverHeight(this.getPopoverHeight());
        this.openDetachedPopover();
        addOutsideClickHandler(this, this.closePopover.bind(this)).then(
            (func) => (this.outsideClickRemover = func)
        );
    }

    /**
     * Closes dropdown popup.
     */
    closePopover() {
        this.control.setAttribute('aria-expanded', 'false');
        this.removeAttribute('data-popover-align');
        this.control.removeAttribute('aria-activedescendant');
        if (!this.isPopoverVisible()) {
            return;
        }
        this.options.removeEventListener('click', this.handleClickOnOption);
        if (this._detachedPopover) {
            this.control.focus(); // Return focus to input field
            this.closeDetachedPopover();
        }
        this.outsideClickRemover?.();
        this.clearHighlight();
    }

    /**
     * Updates content of popover.
     */
    syncOptions() {
        if (this.options) {
            this.rebuildChildren.call(this.options, this.buildOptions());
            this.setPopoverHeight(this.getPopoverHeight());
            this.clearHighlight();
        }
    }

    /**
     * Updates content of popover.
     * @fires event:popover-updated
     */
    updatePopover() {
        this.syncOptions();
        this.setPopoverHeight(this.getPopoverHeight());
        if (
            document.activeElement === this.control &&
            // && this.control.value
            this._prevValue !== this.control.value &&
            this.hasOptions()
        ) {
            if (!this.isPopoverVisible()) {
                this.openPopover();
            } else {
                this.dispatchCustomEvent('popover-updated');
            }
        }
    }
    /**
     * Renders wrapper for one option.
     * @param {HTMLOptGroupElement} group
     * @returns {ElementConfig}
     */
    renderOptionGroup(group) {
        let children = [...group.children];
        if (!this.external) {
            const search = this.control.value;
            children = this.filterOptionsBasedOnInput(children);
        }
        return {
            tagName: 'li',
            classList: {
                'ui-autocomplete__group': true,
                // hide zero options ?
                // '-hidden': this.hideSelected && option.selected
            },
            children: [
                this.renderOption(group),
                {
                    tagName: 'ul',
                    classList: {
                        'ui-autocomplete__group-options': true,
                    },
                    attributes: {
                        'role': 'group',
                        'aria-label': group.label || null,
                    },
                    children: children.map((node, index) =>
                        this.renderOptionWrapper(node, index, children.length)
                    ),
                },
            ],
        };
    }

    /**
     * Renders wrapper for a single option.
     * @param {HTMLOptionElement | HTMLOptGroupElement} option
     * @param {number} index
     * @param {number} total
     * @returns {object}
     */
    renderOptionWrapper(option, index, total = null) {
        return {
            tagName: 'li',
            attributes: {
                'key': option.getAttribute('data-key') || null,
                'role': 'option',
                // These two attributes are only needed when total number of options is not rendered to DOM. Without them, screen readers still announce position and total properly, as long as all options are added to DOM.
                // We leave them for compatibility with future possible support for virtual scrolling.
                'aria-setsize': total !== null ? String(total) : null,
                'aria-posinset': total !== null ? index + 1 : null,
                'aria-label': !isAccountTypeLayout(this.layout)
                    ? option.value || option.innerText
                    : '',
                'aria-selected': 'false',
                'id': option.id || Digest.randomId(),
            },
            classList: {
                'ui-autocomplete__option': true,
            },
            events: {
                keydown: (event) => {
                    if (
                        isKeyPressed(event, keyCodes.SPACE) ||
                        isKeyPressed(event, keyCodes.ENTER)
                    ) {
                        event.target.click();
                    }
                },
            },
            children: [this.renderOption(option)],
        };
    }
    /**
     * Renders one option.
     * @param {HTMLOptionElement | HTMLOptGroupElement} option
     * @returns {ElementConfig}
     */
    renderOption(option) {
        const isGroup = option.localName === 'optgroup';
        const additionalAttributes = {
            'aria-hidden': !isAccountTypeLayout(this.layout),
        };

        const inputText = this.control.value;
        const origValue = option.value || '';
        if (origValue.trim() && inputText.trim()) {
            const reg = new RegExp(inputText, 'gi');
            additionalAttributes['formatted'] = origValue
                .replace(/\*/g, '')
                .replace(reg, '**$&**');
        }

        return {
            tagName: this.option,
            attributes: {
                ...additionalAttributes,
                ...(isGroup
                    ? { optgroup: true, text: option.getAttribute('label') }
                    : this.datalist.getOptionAttributes(option, false)),
            },
        };
    }
    /**
     * Builds options/entries for autocomplete.
     * @returns {Array<ElementConfig>}
     */
    buildOptions() {
        let options = this.datalist
            ? [].slice.call(this.datalist.children, 0)
            : [];
        const inputText = this.control.value;
        const isValid = this.isValueValid();
        if (!this.external) {
            const matchFn = (option) => {
                if (option instanceof HTMLOptGroupElement) {
                    return [].some.call(option.children, matchFn);
                }
                if (!isValid) {
                    return false;
                }
                return inputText ? this.comparator(option, inputText) : true;
            };
            options = [].filter.call(options, matchFn);
        }
        this.control.setAttribute('aria-expanded', String(options.length > 0));
        const total = this.getTotalOptions(options);
        if (this.debounce) {
            setTimeout(() => this.setAssistiveFoundText(total), this.debounce);
        } else {
            this.setAssistiveFoundText(total);
        }
        return options.map((optionItem, index) => {
            if (optionItem instanceof HTMLOptGroupElement) {
                return this.renderOptionGroup(optionItem);
            }
            return this.renderOptionWrapper(optionItem, index, options.length);
        });
    }

    /**
     * Filter option elements based on input value
     * @param {Array<HTMLOptionElement> | HTMLCollection} options
     * @returns {Array<HTMLOptionElement>}
     */
    filterOptionsBasedOnInput(options) {
        const searchQuery = this.control.value;
        return Array.from(options).filter(
            (option) =>
                !searchQuery.trim() || this.comparator(option, searchQuery)
        );
    }

    /**
     * Get total number of filtered options, including ones within optgroups.
     * @param {Array<HTMLOptionElement | HTMLOptGroupElement>} options
     * @returns {number}
     */
    getTotalOptions(options) {
        let count = 0;
        options.forEach((option) => {
            if (option instanceof HTMLOptGroupElement) {
                const children = this.filterOptionsBasedOnInput(
                    option.children
                );
                count += children.length;
            } else {
                count += 1;
            }
        });
        return count;
    }

    /**
     * Sets assistive text how many records found.
     * @param {number} count
     */
    setAssistiveFoundText(count) {
        if (!this.touched) {
            this.assistant.textContent = '';
        } else {
            this.assistant.textContent =
                count > 0
                    ? UIAutocomplete.labels.suggestionsFound + count
                    : UIAutocomplete.labels.noSuggestionsFound;
        }
    }

    /**
     * Fires callback when the input got a focus state.
     * @param {Event} event
     * @private
     */
    handleInputFocus(event) {
        this.touched = true;
        if (this._preventSearch) {
            this._preventSearch = false;
            return;
        }
        if (this.isPopoverVisible()) {
            return;
        }
        this.syncOptions();
        if (this.popoverElement?.querySelector('.ui-autocomplete__option')) {
            this.openPopover();
            if (this.maxlength) {
                this.setPopoverHeight(this.getPopoverHeight());
            }
        }
        this.handleSearchRequest(this.control.value, this.control.name);
    }

    /**
     * Fires callback when the input's value is updated.
     * @param {Event} event
     * @private
     */
    handleInputChange(event) {
        if (!this.isValueValid()) {
            this.closePopover();
            return;
        }
        this.updatePopover();
        const popover = this.popoverElement;

        /* if matches are empty don't show empty popover */
        if (popover && popover.querySelector('.ui-autocomplete__option')) {
            this.openPopover();
            if (this.maxlength) {
                this.setPopoverHeight(this.getPopoverHeight());
            }
        } else {
            this.closePopover();
        }

        this.handleSearchRequest(
            this.control.value,
            this.control.getAttribute('name')
        );
        // remove after any input.
        if (this.hint) {
            this.hint.remove();
            this.removeAttribute('aria-describedby');
        }
    }

    /**
     * Fires callback when debounced search request should be called.
     * @param {string} value
     * @param {string} [key]
     * @private
     */
    handleSearchRequest(value, key) {
        this.dispatchCustomEvent('search', { value: value || '', key: key });
    }

    /**
     * Fires callback when user clicked on option.
     * @param {MouseEvent} event
     * @private
     */
    handleClickOnOption(event) {
        const option = closestByCond(event.target, (node) => {
            return (
                node.nodeType === Node.ELEMENT_NODE &&
                node.classList.contains('ui-autocomplete__option')
            );
        });
        this.pickOption(option);
    }

    /**
     * @param {HTMLElement} element
     * @returns {boolean}
     */
    isInnerElement(element) {
        if (this.isParentOf(element)) {
            return true;
        }
        return !!(
            this._detachedPopover && isParentOf(this._detachedPopover, element)
        );
    }

    /**
     * Fires callback when user clears input.
     * @param {CustomEvent} event
     * @private
     */
    handleClear(event) {
        if (this.isPopoverVisible()) {
            this.closePopover();
        }
    }

    /**
     * Highlights the option at the given index.
     * @param {number} index
     */
    highlightOption(index) {
        const options = this.getOptions();
        [].forEach.call(options, (o, i) => {
            if (i === index) {
                o.classList.add('-active');
                o.setAttribute('aria-selected', 'true');
                this.control.setAttribute('aria-activedescendant', o.id);
            } else {
                o.classList.remove('-active');
                o.setAttribute('aria-selected', 'false');
            }
        });
        this._activeOptionIndex = index;
    }

    /**
     * Clears highlight from all options.
     */
    clearHighlight() {
        const options = this.getOptions();
        [].forEach.call(options, (o) => {
            o.classList.remove('-active');
            o.setAttribute('aria-selected', 'false');
        });
        this.control.removeAttribute('aria-activedescendant');
        this._activeOptionIndex = -1;
        this.setAssistiveFoundText(0);
    }

    /**
     * Handles keyboard events when input is in focus.
     * @param {KeyboardEvent} event
     * @private
     */
    handleKeyboard(event) {
        const options = this.getOptions();
        if (!options.length) {
            return;
        }

        if (
            isKeyPressed(event, keyCodes.UP) ||
            isKeyPressed(event, keyCodes.DOWN)
        ) {
            if (document.activeElement !== this.control) {
                return;
            }

            event.preventDefault();

            const isOpen = this.isPopoverVisible();
            if (!isOpen && isKeyPressed(event, keyCodes.DOWN)) {
                this.openPopover();
            }

            let step = isKeyPressed(event, keyCodes.UP) ? -1 : 1;
            let nextIndex = this._activeOptionIndex + step;

            if (nextIndex < 0) {
                nextIndex = options.length - 1;
            }
            if (nextIndex >= options.length) {
                nextIndex = 0;
            }

            this.highlightOption(nextIndex);
        } else if (isKeyPressed(event, keyCodes.ENTER)) {
            if (
                document.activeElement === this.control &&
                this._activeOptionIndex > -1
            ) {
                event.preventDefault();
                const option = options[this._activeOptionIndex];
                this.pickOption(option);
            }
        } else if (
            isKeyPressed(event, keyCodes.ESCAPE) ||
            isKeyPressed(event, keyCodes.TAB)
        ) {
            this.closePopover();
        }
    }

    /**
     * Pick an option.
     * @param {HTMLLIElement} option
     * @private
     */
    pickOption(option) {
        const uiOptionElement = option?.querySelector(
            '.ui-autocomplete__option > *'
        );
        if (!option || !uiOptionElement) {
            return;
        }
        this.control.value =
            typeof uiOptionElement.getText === 'function'
                ? uiOptionElement.getText()
                : uiOptionElement.getAttribute('value');
        this._prevValue = this.control.value;
        dispatchNativeEvent(this.control, 'change');
        this._preventSearch = false;
        this.control.focus();
        this.search.updateStatus();
        this.closePopover();
        this.clearHighlight();
        this.dispatchCustomEvent('pick', {
            option,
            value: this.control.value,
            data:
                typeof uiOptionElement.getData === 'function'
                    ? uiOptionElement.getData()
                    : {},
        });
    }

    /**
     * Fires callback when datalist got mutations.
     * @param {Event} event
     * @private
     */
    handleDatalistMutations(event) {
        this.updatePopover();
    }

    /**
     * Fires callback when datalist got mutations.
     * @param {Event} event
     * @private
     */
    handlePopoverMutations(event) {
        if (!this.isPopoverVisible()) {
            return;
        }
        this.setPopoverHeight(this.getPopoverHeight());
    }

    /**
     * @inheritDoc
     */
    render() {
        this.touched = false;
        const hintId = `autocomplete-hint-${this.uuid}`;
        const optionsId = `autocomplete-options-${this.uuid}`;
        const assistId = `autocomplete-live-${this.uuid}`;
        const control = this.queryChildren('input')[0];
        const datalist = this.queryChildren('ui-datalist')[0];
        const field = this.closest('ui-field');
        let inputId = control?.id || this.getAttribute('control-id');
        if (field && typeof field.getControlId === 'function') {
            inputId = field.getControlId();
        }
        this.detachChildNodes();
        this.insertElements([
            {
                tagName: 'ui-searchinput',
                attributes: {
                    cleanable: true,
                    searchable: true,
                },
                children: [
                    {
                        tagName: 'input',
                        element: control,
                        attributes: {
                            'id': inputId,
                            'type': 'search',
                            'role': 'combobox',
                            'autocomplete': 'off',
                            'aria-autocomplete': 'list',
                            'aria-expanded': 'false',
                            'aria-describedby': hintId,
                            'aria-controls': optionsId,
                            'tabindex': control?.hasAttribute('readonly')
                                ? TabIndex.Inactive
                                : null,
                        },
                        classList: {
                            'ui-autocomplete__control': true,
                        },
                    },
                ],
            },
            {
                tagName: 'ui-datalist',
                element: datalist,
            },
            {
                tagName: 'div',
                classList: {
                    'ui-autocomplete__popover': true,
                    [`-layout-${this.layout}`]:
                        this.layout === UIAutocomplete.Layout.Search,
                },
                children: [
                    {
                        tagName: 'ul',
                        attributes: {
                            'id': optionsId,
                            'role': 'listbox',
                            'aria-label': UIAutocomplete.labels.suggestions,
                        },
                        classList: {
                            'ui-autocomplete__options': true,
                        },
                    },
                ],
            },
            {
                tagName: 'div',
                attributes: {
                    'id': assistId,
                    'aria-live': 'assertive',
                },
                classList: {
                    'ui-autocomplete__assistant': true,
                    '-visually-hidden': true,
                },
            },
            {
                tagName: 'div',
                attributes: {
                    'id': hintId,
                    'aria-live': 'polite',
                },
                classList: {
                    'ui-autocomplete__hint': true,
                    '-hidden': true,
                },
                children: [UIAutocomplete.labels.hint],
            },
        ]);

        this.updateClassList({
            '-basic': this.type === UIAutocomplete.Type.Basic,
        });
    }

    /**
     * @private
     */
    subscribeEvents() {
        this.control.addEventListener('input', this.handleInputChange);
        this.control.addEventListener('focus', this.handleInputFocus);
        this.control.addEventListener('keydown', this.handleKeyboard);
        this.addEventListener('clear', this.handleClear);
    }

    /**
     * @private
     */
    unsubscribeEvents() {
        this.control.removeEventListener('input', this.handleInputChange);
        this.control.removeEventListener('focus', this.handleInputFocus);
        this.control.removeEventListener('keydown', this.handleKeyboard);
        this.removeEventListener('clear', this.handleClear);
    }

    /**
     * @inheritDoc
     */
    hydrate() {
        appendModalControllerIfRequired();
        this.handleInputChange = this.handleInputChange.bind(this);
        this.handleInputFocus = this.handleInputFocus.bind(this);
        this.handleClear = this.handleClear.bind(this);
        this.handleClickOnOption = this.handleClickOnOption.bind(this);
        this.handleSearchRequest = this.handleSearchRequest.bind(this);
        this.handleKeyboard = this.handleKeyboard.bind(this);
        this.closePopover = this.closePopover.bind(this);
        if (this.debounce) {
            this.handleInputChange = debounce(
                this.handleInputChange,
                this.debounce,
                true
            );
        }

        this.subscribeEvents();
        this.observer = new MutationObserver(
            this.handleDatalistMutations.bind(this)
        );
        this.observer.observe(this.datalist, UIAutocomplete.ObserveOptions);
    }

    /**
     * @inheritDoc
     */
    disconnect() {
        this.closeDetachedPopover();
        this.outsideClickRemover?.();
        this.observer.disconnect();
        this.unsubscribeEvents();
    }

    /**
     * @inheritDoc
     */
    reconnect() {
        this.observer.observe(this.datalist, UIAutocomplete.ObserveOptions);
        this.subscribeEvents();
    }
}

UIAutocomplete.defineElement('ui-autocomplete', styles);
export { UIAutocomplete };
